// Read receipts — a per-user choice, reciprocal by design.
//
// The copy is the feature here. Someone turning this off needs to know before
// they do it that they also stop seeing other people's reads, because that is
// the part they will otherwise discover by accident and feel misled by.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Loader2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import { conversationsApi } from '@/features/conversations/api/conversationsApi';

export const ReadReceiptsCard: React.FC = () => {
  const { t } = useTranslation('settings');
  const [on, setOn] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let stopped = false;
    conversationsApi
      .readReceiptPref()
      .then((v) => {
        if (!stopped) setOn(v);
      })
      // Unreadable: show nothing rather than a toggle whose position is a
      // guess. A privacy control that misreports its own state is worse than
      // one that is briefly absent.
      .catch(() => {
        if (!stopped) setOn(null);
      });
    return () => {
      stopped = true;
    };
  }, []);

  const toggle = async (next: boolean) => {
    const previous = on;
    setOn(next); // optimistic, so the switch does not lag the tap
    setSaving(true);
    try {
      await conversationsApi.setReadReceiptPref(next);
      toast.success(
        next
          ? t('receipts.on_toast', { defaultValue: 'Read receipts on.' })
          : t('receipts.off_toast', {
              defaultValue: "Read receipts off. You won't see other people's either.",
            }),
      );
    } catch (e) {
      setOn(previous); // never leave the switch claiming a state the server rejected
      toast.error(getApiErrorMessage(e, t('receipts.failed', { defaultValue: 'Could not save that.' })));
    } finally {
      setSaving(false);
    }
  };

  if (on === null) return null;

  return (
    <Card data-testid="read-receipts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          {t('receipts.title', { defaultValue: 'Read receipts' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm text-foreground">
              {t('receipts.label', {
                defaultValue: 'Let people see when you have read their messages',
              })}
            </p>
            {/* Said up front, not discovered later. */}
            <p className="text-xs text-muted-foreground">
              {t('receipts.reciprocal', {
                defaultValue:
                  "If you turn this off, you also won't see when other people read yours.",
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('receipts.delivery_note', {
                defaultValue:
                  'Delivery ticks still work either way, and this never changes your unread counts.',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2 pt-0.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />}
            <Switch
              checked={on}
              disabled={saving}
              onCheckedChange={(v) => void toggle(v)}
              data-testid="read-receipts-switch"
              aria-label={t('receipts.label', {
                defaultValue: 'Let people see when you have read their messages',
              })}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
